import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';
import { Plus, Search, Mail, Phone, MapPin, Edit, Eye, Filter, Grid3x3, List, Send, RefreshCw, Download, UserX, X, UserCheck, FileSignature, Loader2, LogOut, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { PersonalExtrasFields } from '../components/employee/PersonalExtrasFields';
import { StatutoryContributionsForm } from '../components/employee/StatutoryContributionsForm';
import { TaxReliefForm } from '../components/employee/TaxReliefForm';
import { lengthOfService } from '../lib/serviceLength';

const EMPLOYEE_EXTRA_DEFAULTS = {
  passport_number: '', passport_expiry: '', visa_reference: '', visa_expiry: '', religion: '', highest_qualification: {},
  statutory_settings: null, tax_reliefs: {},
};

export default function Employees() {
  const [employees, setEmployees] = useState([]);
  const [terminatedEmployees, setTerminatedEmployees] = useState([]);
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [reliefErrors, setReliefErrors] = useState(0);
  const [sortOrder, setSortOrder] = useState('name_asc');
  const [filterStatus, setFilterStatus] = useState('active'); // Default to active only
  const [filterDepartment, setFilterDepartment] = useState('all'); // TC-EMP-011: Department filter
  const [filterPosition, setFilterPosition] = useState('all'); // TC-EMP-014: Position filter
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [viewMode, setViewMode] = useState('list'); // Default to list view
  const [activeTab, setActiveTab] = useState('active'); // 'active' or 'terminated'
  // Iter88k — Resignation requests review for HR.
  const [resignations, setResignations] = useState([]);
  const [resignReviewDialog, setResignReviewDialog] = useState(null); // selected resignation
  const [resignReviewMode, setResignReviewMode] = useState(null); // 'approve' | 'reject'
  const [resignReviewNote, setResignReviewNote] = useState('');
  const [resignReviewNotice, setResignReviewNotice] = useState(30); // Iter88p — HR-editable notice period
  const [resignReviewSubmitting, setResignReviewSubmitting] = useState(false);
  // TC-EMP-002: Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [formData, setFormData] = useState({
    user_id: '',
    full_name: '',
    email: '',
    phone: '',
    department: '',
    position: '',
    join_date: new Date().toISOString().split('T')[0],
    employment_type: 'full_time',
    status: 'pending_joining',  // pre-boarding by default — HR flips to 'active' on join day
    salary_basic: 0,
    salary_allowances: 0,
    // Iter129 — structured allowance components
    transport_allowance: 0,
    meal_allowance: 0,
    housing_allowance: 0,
    other_allowance: 0,
    notice_period_days: '', // Iter88p — per-employee notice period override (empty = use company policy)
    ic_number: '',
    nationality: 'malaysian',
    marital_status: 'single',
    spouse_working: false,
    number_of_children: 0,
    disabled_self: false,
    disabled_spouse: false,
    disabled_children: 0,
    epf_number: '',
    socso_number: '',
    bank_name: '',
    bank_account: '',
    visa_reference: '', // New field for non-Malaysians
    manager_id: '', // TC-EMP-032: Reporting Manager field
    // Iter54 (Ankur Bug A) — Access level / role assigned to the user
    // account created on invitation activation. 'employee' = standard
    // employee dashboard only; 'manager' = can approve subordinate leave
    // and claims for everyone whose manager_id == this employee.
    role: 'employee',
    ...EMPLOYEE_EXTRA_DEFAULTS,
  });
  const [sendInvitation, setSendInvitation] = useState(true);
  const [resendingId, setResendingId] = useState(null);
  const [activatingId, setActivatingId] = useState(null);
  // Iter66 — inline "Send Offer Letter" flow for pending_joining rows
  const [offerLetterModal, setOfferLetterModal] = useState({ open: false, employee: null });
  const [offerLetterFile, setOfferLetterFile] = useState(null);
  const [offerLetterMessage, setOfferLetterMessage] = useState('');
  const [offerSending, setOfferSending] = useState(false);
  const [offerResult, setOfferResult] = useState(null);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const user = JSON.parse(localStorage.getItem('hrms_user') || sessionStorage.getItem('hrms_user') || '{}');
  const canManage = ['admin', 'hr_manager'].includes(user.role);

  // Deep link from Dashboard Quick Actions (/dashboard/employees?add=1) opens the Add Employee dialog.
  useEffect(() => {
    if (searchParams.get('add') === '1' && canManage) {
      resetForm();
      setDialogOpen(true);
      searchParams.delete('add');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams]);

  // Get unique departments and positions for filters
  const departments = [...new Set(employees.map(e => e.department).filter(Boolean))];
  const positions = [...new Set(employees.map(e => e.position).filter(Boolean))];

  const handleResendInvitation = async (e, employeeId) => {
    e.stopPropagation();
    setResendingId(employeeId);
    try {
      const token = localStorage.getItem('hrms_token');
      await axios.post(`${API}/employees/${employeeId}/resend-invitation`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Login invitation email sent successfully!');
      fetchEmployees();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send login invitation');
    } finally {
      setResendingId(null);
    }
  };

  // Iter67 — programmatic version (no event) used in chained flows like
  // "Activate then send login invitation". Returns true on success so the
  // caller can decide whether to refresh data.
  const sendLoginInvitation = async (employeeId) => {
    setResendingId(employeeId);
    try {
      const token = localStorage.getItem('hrms_token');
      await axios.post(`${API}/employees/${employeeId}/resend-invitation`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Login invitation email sent successfully!');
      return true;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send login invitation');
      return false;
    } finally {
      setResendingId(null);
    }
  };

  // Activate a pending_joining or inactive employee.
  // Iter64 — adds a one-click HR action. Two-step confirm:
  //   1) Standard "Activate this employee?" prompt.
  //   2) Extra warning if the employee still has any unsigned offer letter
  //      (document where status is sent / partially_signed and the signer
  //      row points to this employee). HR can override and proceed.
  const handleActivateEmployee = async (e, employee) => {
    e.stopPropagation();
    if (!canManage) return;
    if (employee.status === 'terminated') {
      toast.error('Terminated employees cannot be activated directly. Re-hire them as a new employee.');
      return;
    }

    // Step 1: check for unsigned offer letters (best-effort)
    let hasUnsignedOffer = false;
    try {
      const token = localStorage.getItem('hrms_token');
      // Fetch any docs assigned to this employee that are still awaiting signature
      // (sent or partially_signed). The endpoint is HR-only so canManage is true here.
      const res = await axios.get(`${API}/documents`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const docs = Array.isArray(res.data) ? res.data : [];
      hasUnsignedOffer = docs.some((d) => {
        if (!['sent', 'partially_signed'].includes(d.status)) return false;
        return (d.signers || []).some((s) => {
          if (s.employee_id === employee.employee_id) return s.status !== 'signed';
          if (s.email && employee.email && s.email.toLowerCase() === employee.email.toLowerCase()) {
            return s.status !== 'signed';
          }
          return false;
        });
      });
    } catch {
      // best-effort — don't block activation if the check fails
    }

    const primaryPrompt = `Activate ${employee.full_name}? They will get normal employee access to leave, attendance, claims, payslips, and dashboard.`;
    if (!window.confirm(primaryPrompt)) return;
    if (hasUnsignedOffer) {
      const warn = `${employee.full_name} still has an offer letter that is NOT signed yet. Are you sure you want to activate them now?`;
      if (!window.confirm(warn)) return;
    }

    setActivatingId(employee.employee_id);
    try {
      const token = localStorage.getItem('hrms_token');
      await axios.put(
        `${API}/employees/${employee.employee_id}`,
        { status: 'active' },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(`${employee.full_name} is now Active.`);
      // Iter67 — chained UX: if this employee has no login account yet
      // (either no user_id or no orphan user record by email), offer to
      // send the login invitation right away so HR doesn't need a separate
      // step. We accept user_id falsy as a fast-path; the backend will
      // reject the resend gracefully if a user actually exists by email.
      const stillNeedsLogin = employee.has_user_account === false || !employee.user_id;
      if (stillNeedsLogin) {
        const askInvite = window.confirm(
          `Activated! ${employee.full_name} does not have a login yet. ` +
          `Send them the login invitation email now so they can sign in?`
        );
        if (askInvite) {
          await sendLoginInvitation(employee.employee_id);
        }
      }
      fetchEmployees();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to activate employee');
    } finally {
      setActivatingId(null);
    }
  };

  // Iter66 — Send Offer Letter inline from the Employees list.
  // 3-step pipeline behind ONE click:
  //   1) POST /api/documents/upload  → file_id + pdf_filename
  //   2) POST /api/documents (form-encoded) with employee pre-filled as signer
  //   3) POST /api/documents/{id}/send → email + in-app notification
  // The signer auto-link logic from Iter63 stamps employee_id on the signer row.
  const openOfferLetterModal = (e, employee) => {
    e.stopPropagation();
    setOfferLetterModal({ open: true, employee });
    setOfferLetterFile(null);
    setOfferLetterMessage(`Welcome to the team! Please review and sign your offer letter.`);
    setOfferResult(null);
  };

  const closeOfferLetterModal = () => {
    if (offerSending) return; // never close mid-send
    setOfferLetterModal({ open: false, employee: null });
    setOfferLetterFile(null);
    setOfferLetterMessage('');
    setOfferResult(null);
  };

  const handleSendOfferLetter = async () => {
    const { employee } = offerLetterModal;
    if (!employee) return;
    if (!offerLetterFile) {
      toast.error('Please pick a PDF offer letter to send.');
      return;
    }
    setOfferSending(true);
    const token = localStorage.getItem('hrms_token');
    try {
      // Step 1: upload
      const fd = new FormData();
      fd.append('file', offerLetterFile);
      const upRes = await axios.post(`${API}/documents/upload`, fd, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const fileId = upRes.data?.file_id;
      const pdfFilename = upRes.data?.pdf_filename;
      if (!fileId || !pdfFilename) {
        throw new Error('Upload did not return file_id / pdf_filename');
      }

      // Step 2: create the document with the employee pre-filled as the only signer
      const docForm = new FormData();
      docForm.append('title', `Offer Letter — ${employee.full_name}`);
      docForm.append('document_type', 'offer_letter');
      if (offerLetterMessage) docForm.append('message', offerLetterMessage);
      docForm.append('file_id', fileId);
      docForm.append('pdf_filename', pdfFilename);
      docForm.append(
        'signers',
        JSON.stringify([
          {
            name: employee.full_name,
            email: employee.email,
            order: 1,
            // No signature_fields placed — backend falls back to the default
            // bottom-of-last-page signature panel (Iter58).
            signature_fields: [],
          },
        ]),
      );
      const docRes = await axios.post(`${API}/documents`, docForm, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const docId = docRes.data?.document_id;
      if (!docId) throw new Error('Document creation did not return a document_id');

      // Step 3: send for signature
      const sendRes = await axios.post(
        `${API}/documents/${docId}/send`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const summary = sendRes.data || {};
      setOfferResult({
        document_id: docId,
        summary_status: summary.summary_status,
        summary_message: summary.summary_message,
        email_results: summary.email_results || [],
      });

      // Surface a toast that matches the email reality
      if (summary.summary_status === 'sent') {
        toast.success('Offer letter sent. Email delivered.');
      } else if (summary.summary_status === 'disabled') {
        toast.warning('Offer letter saved. Email is not configured — share the link with the candidate manually.');
      } else if (summary.summary_status === 'failed') {
        toast.error('Offer letter saved but email could not be delivered. Use the link to share manually.');
      } else {
        toast.success('Offer letter sent.');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || error.message || 'Failed to send offer letter');
    } finally {
      setOfferSending(false);
    }
  };

  useEffect(() => {
    fetchEmployees();
    fetchResignations();
  }, []);

  // Iter88k — Resignation list + review helpers for HR.
  const fetchResignations = async () => {
    try {
      const r = await axios.get(`${API}/resignations`);
      setResignations(Array.isArray(r.data) ? r.data : []);
    } catch (_) {
      setResignations([]);
    }
  };

  const openResignReview = (resignation, mode) => {
    setResignReviewDialog(resignation);
    setResignReviewMode(mode);
    setResignReviewNote('');
    setResignReviewNotice(Number(resignation?.notice_period_days) || 30);
  };

  const submitResignReview = async () => {
    if (!resignReviewDialog || !resignReviewMode) return;
    if (resignReviewMode === 'reject' && !resignReviewNote.trim()) {
      toast.error('Please provide a reason for rejection.');
      return;
    }
    setResignReviewSubmitting(true);
    try {
      // Iter88p — On approve, HR may override the notice period.
      const body = { hr_response: resignReviewNote };
      if (resignReviewMode === 'approve') {
        body.notice_period_days = Number(resignReviewNotice) || 0;
      }
      await axios.post(
        `${API}/resignations/${resignReviewDialog.resignation_id}/${resignReviewMode}`,
        body
      );
      toast.success(
        resignReviewMode === 'approve'
          ? 'Resignation approved. Employee marked as terminated.'
          : 'Resignation rejected. Employee will be notified.'
      );
      setResignReviewDialog(null);
      setResignReviewMode(null);
      setResignReviewNote('');
      fetchEmployees();
      fetchResignations();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to action resignation.');
    } finally {
      setResignReviewSubmitting(false);
    }
  };

  // Close filter dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.relative')) {
        setShowFilterDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    // TC-EMP-007: Search by IC number — match with or without dashes
    const q = searchQuery.toLowerCase();
    const qDigits = q.replace(/[^0-9]/g, '');
    let filtered = employees.filter(emp => {
      const icDigits = (emp.ic_number || '').replace(/[^0-9]/g, '');
      return (
        emp.full_name.toLowerCase().includes(q) ||
        emp.email.toLowerCase().includes(q) ||
        emp.department?.toLowerCase().includes(q) ||
        emp.employee_id.toLowerCase().includes(q) ||
        (emp.ic_number || '').toLowerCase().includes(q) ||
        (qDigits.length >= 4 && icDigits.includes(qDigits))
      );
    });

    // Apply status filter
    if (filterStatus !== 'all') {
      filtered = filtered.filter(emp => emp.status === filterStatus);
    }

    // TC-EMP-011: Apply department filter
    if (filterDepartment !== 'all') {
      filtered = filtered.filter(emp => emp.department === filterDepartment);
    }

    // TC-EMP-014: Apply position filter
    if (filterPosition !== 'all') {
      filtered = filtered.filter(emp => emp.position === filterPosition);
    }

    // Apply sorting
    filtered.sort((a, b) => {
      switch (sortOrder) {
        case 'name_asc':
          return a.full_name.localeCompare(b.full_name);
        case 'name_desc':
          return b.full_name.localeCompare(a.full_name);
        case 'department_asc':
          return (a.department || '').localeCompare(b.department || '');
        case 'department_desc':
          return (b.department || '').localeCompare(a.department || '');
        case 'join_date_newest':
          return new Date(b.join_date) - new Date(a.join_date);
        case 'join_date_oldest':
          return new Date(a.join_date) - new Date(b.join_date);
        default:
          return 0;
      }
    });

    setFilteredEmployees(filtered);
    setCurrentPage(1); // Reset to first page when filters change
  }, [searchQuery, employees, sortOrder, filterStatus, filterDepartment, filterPosition]);

  const fetchEmployees = async () => {
    try {
      const response = await axios.get(`${API}/employees`);
      // Separate active and terminated employees
      const active = response.data.filter(emp => emp.status !== 'terminated');
      const terminated = response.data.filter(emp => emp.status === 'terminated');
      setEmployees(active);
      setTerminatedEmployees(terminated);
      setFilteredEmployees(active);
    } catch (error) {
      toast.error('Failed to fetch employees');
    } finally {
      setLoading(false);
    }
  };

  // Export terminated employees to Excel/CSV
  const exportTerminatedToExcel = () => {
    if (terminatedEmployees.length === 0) {
      toast.error('No terminated employees to export');
      return;
    }
    
    const headers = ['Employee ID', 'Full Name', 'Email', 'Department', 'Position', 'Join Date', 'Termination Date', 'Termination Type', 'Reason', 'Last Working Day', 'Final Settlement'];
    const rows = terminatedEmployees.map(emp => [
      emp.employee_id,
      emp.full_name,
      emp.email,
      emp.department || 'N/A',
      emp.position || 'N/A',
      emp.join_date ? new Date(emp.join_date).toLocaleDateString('en-MY') : 'N/A',
      emp.termination_details?.termination_date ? new Date(emp.termination_details.termination_date).toLocaleDateString('en-MY') : 'N/A',
      emp.termination_details?.termination_type || 'N/A',
      emp.termination_details?.reason_for_leaving || 'N/A',
      emp.termination_details?.last_working_day ? new Date(emp.termination_details.last_working_day).toLocaleDateString('en-MY') : 'N/A',
      emp.termination_details?.final_settlement_amount ? `RM ${emp.termination_details.final_settlement_amount.toFixed(2)}` : 'N/A'
    ]);
    
    const csvContent = [headers.join(','), ...rows.map(r => r.map(c => `"${c}"`).join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `terminated_employees_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    toast.success('Terminated employees exported to CSV');
  };

  // TC-EMP-042: Export all employees to CSV
  const exportAllEmployees = () => {
    const allEmps = activeTab === 'active' ? filteredEmployees : terminatedEmployees;
    if (allEmps.length === 0) {
      toast.error('No employees to export');
      return;
    }
    
    const headers = ['Employee ID', 'Full Name', 'Email', 'IC Number', 'Department', 'Position', 'Phone', 'Join Date', 'Length of Service', 'Status', 'Employment Type', 'Basic Salary'];
    const rows = allEmps.map(emp => [
      emp.employee_id,
      emp.full_name,
      emp.email,
      emp.ic_number || 'N/A',
      emp.department || 'N/A',
      emp.position || 'N/A',
      emp.phone || 'N/A',
      emp.join_date ? new Date(emp.join_date).toLocaleDateString('en-MY') : 'N/A',
      lengthOfService(emp.join_date, emp.status === 'terminated' ? emp.termination_details?.last_working_day : null)?.label || 'N/A',
      emp.status || 'active',
      emp.employment_type || 'full_time',
      emp.salary_basic || 0
    ]);
    
    const csvContent = [headers.join(','), ...rows.map(r => r.map(c => `"${c}"`).join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `employees_${activeTab}_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    toast.success(`${allEmps.length} employees exported to CSV`);
  };

  // TC-EMP-016: Clear all filters
  const clearFilters = () => {
    setSearchQuery('');
    setFilterStatus('active');
    setFilterDepartment('all');
    setFilterPosition('all');
    setSortOrder('name_asc');
    setCurrentPage(1);
    toast.success('Filters cleared');
  };

  // Check if any filters are active
  const hasActiveFilters = searchQuery || filterStatus !== 'active' || filterDepartment !== 'all' || filterPosition !== 'all' || sortOrder !== 'name_asc';

  // TC-EMP-002: Pagination calculations
  const totalPages = Math.ceil(filteredEmployees.length / itemsPerPage);
  const paginatedEmployees = filteredEmployees.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const resetForm = () => {
    setFormData({
      user_id: '',
      full_name: '',
      email: '',
      phone: '',
      department: '',
      position: '',
      join_date: new Date().toISOString().split('T')[0],
      employment_type: 'full_time',
      status: 'pending_joining',
      salary_basic: 0,
      salary_allowances: 0,
      transport_allowance: 0,
      meal_allowance: 0,
      housing_allowance: 0,
      other_allowance: 0,
      notice_period_days: '',
      ic_number: '',
      nationality: 'malaysian',
      marital_status: 'single',
      spouse_working: false,
      number_of_children: 0,
      disabled_self: false,
      disabled_spouse: false,
      disabled_children: 0,
      epf_number: '',
      socso_number: '',
      bank_name: '',
      bank_account: '',
      manager_id: '', // TC-EMP-032
      ...EMPLOYEE_EXTRA_DEFAULTS,
    });
    setSendInvitation(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Client-side validation (TC-EMP-019..028)
    if (reliefErrors > 0) {
      toast.error(`${reliefErrors} tax relief amount(s) exceed the LHDN limit. Reduce them to the maximum shown before saving.`);
      return;
    }
    if (!(formData.full_name || '').trim()) {
      toast.error('Full name is required');
      return;
    }
    const emailRegex = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
    if (!emailRegex.test((formData.email || '').trim())) {
      toast.error('Invalid email format');
      return;
    }
    if (formData.ic_number) {
      const icClean = formData.ic_number.replace(/[-\s]/g, '');
      if (/^0+$/.test(icClean)) {
        toast.error('Invalid IC number — cannot be all zeros');
        return;
      }
      if (formData.nationality === 'malaysian' && !/^\d{12}$/.test(icClean)) {
        toast.error('Invalid Malaysian IC. Must be 12 digits (YYMMDD-PB-XXXX)');
        return;
      }
      const yy = parseInt(icClean.slice(0, 2), 10);
      const mm = parseInt(icClean.slice(2, 4), 10);
      const dd = parseInt(icClean.slice(4, 6), 10);
      const fullYear = yy > (new Date().getFullYear() % 100) ? 1900 + yy : 2000 + yy;
      const probe = new Date(fullYear, mm - 1, dd);
      if (probe.getFullYear() !== fullYear || probe.getMonth() !== mm - 1 || probe.getDate() !== dd) {
        toast.error('Invalid IC — date in IC is not a real calendar date');
        return;
      }
    }
    if (formData.phone) {
      const phoneDigits = formData.phone.replace(/[\s\-()]/g, '');
      const phoneValid = /^\+?60\d{8,11}$/.test(phoneDigits) ||
                         /^0\d{8,10}$/.test(phoneDigits) ||
                         /^\+\d{8,15}$/.test(phoneDigits);
      if (!phoneValid) {
        toast.error('Invalid phone number. Use Malaysian format (e.g. +60123456789)');
        return;
      }
    }
    // TC-EMP-026: Future join dates are ALLOWED (for offer letters / future starters)
    // No date check here. Only enforce parseable format via the HTML input.
    // Iter95 (Ankur 16-Jul-2026) — Enforce non-zero salary. Zero salary
    // was silently accepted before and caused RM 0.00 payslips.
    const salary = parseFloat(formData.salary_basic) || 0;
    if (salary <= 0) {
      toast.error('Basic salary must be greater than 0');
      return;
    }
    if (salary < 1700) {
      toast.error('Basic salary is below Malaysia minimum wage (RM 1,700)');
      return;
    }

    try {
      // Iter129 — structured allowance buckets (legacy single field is
      // derived from the sum for backward compat).
      const transport = parseFloat(formData.transport_allowance) || 0;
      const meal = parseFloat(formData.meal_allowance) || 0;
      const housing = parseFloat(formData.housing_allowance) || 0;
      const other = parseFloat(formData.other_allowance) || 0;
      const totalAllowances = +(transport + meal + housing + other).toFixed(2);
      const payload = {
        ...formData,
        salary_basic: parseFloat(formData.salary_basic) || 0,
        salary_allowances: totalAllowances,
        transport_allowance: transport,
        meal_allowance: meal,
        housing_allowance: housing,
        other_allowance: other,
        // Iter88p — empty string ⇒ null so backend falls back to company policy
        notice_period_days:
          formData.notice_period_days === '' || formData.notice_period_days === null
            ? null
            : (parseInt(formData.notice_period_days, 10) || 0),
        passport_number: formData.passport_number || null,
        passport_expiry: formData.passport_expiry || null,
        visa_reference: formData.visa_reference || null,
        visa_expiry: formData.visa_expiry || null,
        religion: formData.religion || null,
        highest_qualification: Object.keys(formData.highest_qualification || {}).length ? formData.highest_qualification : null,
        statutory_settings: formData.statutory_settings || null,
        tax_reliefs: formData.tax_reliefs || {},
        send_invitation: sendInvitation
      };
      
      // Only add new employee (edit is handled by separate page)
      await axios.post(`${API}/employees`, payload);
      toast.success('Employee added successfully!');
      
      setDialogOpen(false);
      fetchEmployees();
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add employee');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Iter88k — Pending Resignation Requests banner for HR */}
      {(() => {
        const pending = (resignations || []).filter((r) => r.status === 'pending');
        if (pending.length === 0) return null;
        return (
          <div
            data-testid="pending-resignations-banner"
            className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700/40 rounded-xl overflow-hidden"
          >
            <div className="p-4 border-b border-red-200 dark:border-red-700/40 flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400" />
              <h4 className="font-semibold text-red-800 dark:text-red-200">
                {pending.length} pending resignation request{pending.length > 1 ? 's' : ''} awaiting your review
              </h4>
            </div>
            <div className="divide-y divide-red-200 dark:divide-red-700/40">
              {pending.map((r) => (
                <div key={r.resignation_id} className="p-4 flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-slate-900 dark:text-slate-100">{r.employee_name}</span>
                      <span className="text-xs text-slate-500">({r.employee_id})</span>
                    </div>
                    <p className="text-sm text-slate-700 dark:text-slate-300 mt-1">
                      <strong>Last working day:</strong> {r.last_working_day}
                      {' · '}<strong>Notice:</strong> {r.notice_period_days || 0} day(s)
                    </p>
                    <p className="text-sm text-slate-700 dark:text-slate-300 mt-1">
                      <em>Reason:</em> {r.reason}
                    </p>
                    <p className="text-xs text-slate-500 mt-1">
                      Submitted {new Date(r.submitted_at).toLocaleString('en-MY')}
                    </p>
                  </div>
                  <div className="flex flex-col gap-2">
                    <Button
                      size="sm"
                      onClick={() => openResignReview(r, 'approve')}
                      data-testid={`approve-resignation-${r.resignation_id}`}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white"
                    >
                      Approve
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => openResignReview(r, 'reject')}
                      data-testid={`reject-resignation-${r.resignation_id}`}
                      className="border-red-300 text-red-600 hover:bg-red-50"
                    >
                      Reject
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })()}

      {/* Header */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-100">Employees</h2>
          <p className="text-slate-600 dark:text-slate-400 mt-1">
            {activeTab === 'active' 
              ? `${employees.length} active employees` 
              : `${terminatedEmployees.length} terminated employees`}
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {/* Tab Toggle */}
          <div className="flex bg-slate-100 dark:bg-slate-800 rounded-lg p-1">
            <button
              onClick={() => { setActiveTab('active'); setFilterStatus('all'); }}
              data-testid="tab-active"
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                activeTab === 'active' && filterStatus !== 'pending_joining'
                  ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 shadow-sm'
                  : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100'
              }`}
            >
              Active ({employees.length})
            </button>
            <button
              onClick={() => { setActiveTab('active'); setFilterStatus('pending_joining'); }}
              data-testid="tab-pending-joining"
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center gap-2 ${
                activeTab === 'active' && filterStatus === 'pending_joining'
                  ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 shadow-sm'
                  : 'text-slate-600 dark:text-slate-400 hover:text-amber-700 dark:hover:text-amber-300'
              }`}
              title="Show pre-boarding / pending-joining employees only"
            >
              <UserCheck className="h-4 w-4" />
              Pending Joining ({employees.filter((e) => e.status === 'pending_joining').length})
            </button>
            <button
              onClick={() => setActiveTab('terminated')}
              data-testid="tab-terminated"
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center gap-2 ${
                activeTab === 'terminated'
                  ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 shadow-sm'
                  : 'text-slate-600 dark:text-slate-400 hover:text-red-600 dark:hover:text-red-400'
              }`}
            >
              <UserX className="h-4 w-4" />
              Terminated ({terminatedEmployees.length})
            </button>
          </div>
          
          {/* Export button for terminated */}
          {activeTab === 'terminated' && terminatedEmployees.length > 0 && (
            <Button
              variant="outline"
              onClick={exportTerminatedToExcel}
              className="gap-2"
            >
              <Download className="h-4 w-4" />
              Export CSV
            </Button>
          )}

          {/* TC-EMP-048 / TC-EMP-049: Bulk Import button */}
          {canManage && activeTab === 'active' && (
            <BulkImportButton onImported={() => fetchEmployees()} />
          )}

          {canManage && activeTab === 'active' && (
          <Dialog open={dialogOpen} onOpenChange={(open) => {
            setDialogOpen(open);
            if (!open) resetForm();
          }}>
            <DialogTrigger asChild>
              <Button 
                className="bg-slate-900 hover:bg-slate-800" 
                data-testid="add-employee-btn"
                onClick={() => {
                  resetForm();
                  setDialogOpen(true);
                }}
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Employee
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-5xl h-[85vh] p-0 flex flex-col">
              <DialogHeader className="px-6 pt-6 pb-4 border-b border-slate-200 dark:border-slate-700 shrink-0">
                <DialogTitle className="text-xl font-bold">Add New Employee</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0" data-testid="add-employee-form">
                <div className="overflow-y-scroll flex-1 px-6 py-6 space-y-6" style={{maxHeight: 'calc(85vh - 140px)'}}>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Full Name</Label>
                    <Input
                      required
                      name="full_name"
                      data-testid="add-employee-full-name-input"
                      value={formData.full_name}
                      onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                      placeholder="John Doe"
                    />
                  </div>
                  <div>
                    <Label>Email</Label>
                    <Input
                      required
                      type="email"
                      name="email"
                      data-testid="add-employee-email-input"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="john@company.com"
                    />
                  </div>
                  <div>
                    <Label>Phone</Label>
                    <Input
                      name="phone"
                      data-testid="add-employee-phone-input"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      placeholder="+60123456789"
                    />
                  </div>
                  <div>
                    <Label>NRIC No.</Label>
                    <Input
                      name="ic_number"
                      data-testid="add-employee-ic-input"
                      value={formData.ic_number}
                      onChange={(e) => setFormData({ ...formData, ic_number: e.target.value })}
                      placeholder="990101-01-1234"
                    />
                  </div>
                  <div>
                    <Label>Nationality</Label>
                    <select
                      value={formData.nationality}
                      onChange={(e) => setFormData({ ...formData, nationality: e.target.value, statutory_settings: null })}
                      className="w-full h-10 rounded-md border border-slate-300 px-3"
                      data-testid="add-employee-nationality"
                    >
                      <option value="malaysian">Malaysian</option>
                      <option value="non_malaysian_permanent">Non-Malaysian (Permanent Resident)</option>
                      <option value="non_malaysian_temporary">Non-Malaysian (Temporary)</option>
                    </select>
                  </div>
                  {/* Visa Reference - Required for non-Malaysians */}
                  <PersonalExtrasFields form={formData} onChange={(patch) => setFormData((f) => ({ ...f, ...patch }))} />
                  <div>
                    <Label>Department</Label>
                    <Input
                      value={formData.department}
                      onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                      placeholder="Engineering"
                    />
                  </div>
                  <div>
                    <Label>Position</Label>
                    <Input
                      value={formData.position}
                      onChange={(e) => setFormData({ ...formData, position: e.target.value })}
                      placeholder="Senior Developer"
                    />
                  </div>
                  {/* TC-EMP-032: Reporting Manager Field */}
                  <div>
                    <Label>Reporting Manager</Label>
                    <select
                      data-testid="manager-select"
                      value={formData.manager_id}
                      onChange={(e) => setFormData({ ...formData, manager_id: e.target.value })}
                      className="w-full h-10 rounded-md border border-slate-300 dark:border-slate-600 px-3 bg-white dark:bg-slate-800"
                    >
                      <option value="">Select Manager (Optional)</option>
                      {/* Iter83 — Anyone active in the same company can be
                          a Reporting Manager. Previously this filtered by
                          `e.role` but Employee docs don't carry a role
                          field, so the dropdown was always empty. */}
                      {employees
                        .filter((e) => e.employee_id !== formData.employee_id && e.status === 'active')
                        .sort((a, b) => (a.full_name || '').localeCompare(b.full_name || ''))
                        .map((mgr) => (
                        <option key={mgr.employee_id} value={mgr.employee_id}>
                          {mgr.full_name}{mgr.position ? ` — ${mgr.position}` : ''}{mgr.department ? ` · ${mgr.department}` : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                  {/* Iter54 (Ankur Bug A) — Access Level / Role assignment.
                      Previously HR had no way to mark a new hire as a
                      Manager during onboarding; everyone got 'employee'
                      access regardless of their position. The role chosen
                      here flows into the invitation and is set on the user
                      account when the employee activates. */}
                  <div>
                    <Label>Access Level / Role</Label>
                    <select
                      data-testid="add-employee-role-select"
                      value={formData.role}
                      onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                      className="w-full h-10 rounded-md border border-slate-300 dark:border-slate-600 px-3 bg-white dark:bg-slate-800"
                    >
                      <option value="employee">Employee — standard dashboard only</option>
                      <option value="manager">Manager — can approve subordinates' leave / claims</option>
                      <option value="hr_user">HR User — read-only HR module access</option>
                      <option value="payroll_officer">Payroll Officer — payroll module only</option>
                    </select>
                    <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-1">
                      To grant Admin or HR-Manager access, ask a Super Admin to update the user in
                      the Platform Console after activation.
                    </p>
                  </div>
                  <div>
                    <Label>Join Date</Label>
                    <Input
                      required
                      type="date"
                      name="join_date"
                      data-testid="add-employee-join-date-input"
                      value={formData.join_date}
                      onChange={(e) => setFormData({ ...formData, join_date: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>Employment Type</Label>
                    <select
                      value={formData.employment_type}
                      onChange={(e) => setFormData({ ...formData, employment_type: e.target.value })}
                      className="w-full h-10 rounded-md border border-slate-300 px-3"
                    >
                      <option value="full_time">Full Time</option>
                      <option value="part_time">Part Time</option>
                      <option value="contract">Contract</option>
                    </select>
                  </div>
                  <div>
                    <Label>Employment Status</Label>
                    <select
                      value={formData.status || 'pending_joining'}
                      onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                      className="w-full h-10 rounded-md border border-slate-300 px-3"
                      data-testid="employee-status-select"
                    >
                      <option value="pending_joining">Pending Joining (Pre-boarding)</option>
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                      <option value="terminated">Terminated</option>
                    </select>
                    <p className="text-xs text-slate-500 mt-1">
                      Pending Joining staff can receive offer letters but are excluded from payroll, leave and attendance until activated.
                    </p>
                  </div>
                  <div>
                    <Label>Basic Salary (RM) <span className="text-red-500">*</span></Label>
                    <Input
                      required
                      type="number"
                      step="0.01"
                      min="1700"
                      max="500000"
                      name="salary_basic"
                      data-testid="add-employee-salary-input"
                      value={formData.salary_basic}
                      onChange={(e) => setFormData({ ...formData, salary_basic: e.target.value })}
                      placeholder="5000.00"
                    />
                    {/* Iter95 (Ankur 16-Jul-2026) — Min 1,700 (Malaysia
                        statutory minimum wage). Max 500,000/mo as an upper
                        sanity limit. Prevents zero-salary payslips. */}
                    <p className="text-xs text-slate-500 mt-1">
                      Minimum: RM 1,700/month (Malaysia statutory minimum wage).
                    </p>
                  </div>
                  <div>
                    <Label>Transport Allowance (RM)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      data-testid="add-employee-transport-allowance"
                      value={formData.transport_allowance}
                      onChange={(e) => setFormData({ ...formData, transport_allowance: e.target.value })}
                      placeholder="0.00"
                    />
                  </div>
                  <div>
                    <Label>Meal Allowance (RM)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      data-testid="add-employee-meal-allowance"
                      value={formData.meal_allowance}
                      onChange={(e) => setFormData({ ...formData, meal_allowance: e.target.value })}
                      placeholder="0.00"
                    />
                  </div>
                  <div>
                    <Label>Housing Allowance (RM)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      data-testid="add-employee-housing-allowance"
                      value={formData.housing_allowance}
                      onChange={(e) => setFormData({ ...formData, housing_allowance: e.target.value })}
                      placeholder="0.00"
                    />
                  </div>
                  <div>
                    <Label>Other Allowance (RM)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      data-testid="add-employee-other-allowance"
                      value={formData.other_allowance}
                      onChange={(e) => setFormData({ ...formData, other_allowance: e.target.value })}
                      placeholder="0.00"
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      Total Allowances: <strong>
                        RM {(
                          (parseFloat(formData.transport_allowance) || 0)
                          + (parseFloat(formData.meal_allowance) || 0)
                          + (parseFloat(formData.housing_allowance) || 0)
                          + (parseFloat(formData.other_allowance) || 0)
                        ).toFixed(2)}
                      </strong>
                    </p>
                  </div>
                  <div>
                    <Label>Notice Period (days)</Label>
                    <Input
                      type="number"
                      min="0"
                      data-testid="add-employee-notice-period-input"
                      value={formData.notice_period_days}
                      onChange={(e) => setFormData({ ...formData, notice_period_days: e.target.value })}
                      placeholder="Leave blank to use company policy"
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      Overrides company HR policy for this employee only.
                      Useful for probation (7 days), senior staff (60 days), or contract-specific terms.
                      Leave blank to use the company default.
                    </p>
                  </div>
                </div>

                {/* Tax Relief Information */}
                <div className="border-t border-slate-200 pt-4 mt-4">
                  <h4 className="font-semibold text-slate-900 mb-3">Tax Relief Information (for PCB Calculation)</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Marital Status</Label>
                      <select
                        value={formData.marital_status}
                        onChange={(e) => setFormData({ ...formData, marital_status: e.target.value })}
                        className="w-full h-10 rounded-md border border-slate-300 px-3"
                      >
                        <option value="single">Single</option>
                        <option value="married">Married</option>
                      </select>
                    </div>
                    {formData.marital_status === 'married' && (
                      <>
                        <div className="flex items-center space-x-2 pt-6">
                          <input
                            type="checkbox"
                            id="spouse_working"
                            checked={formData.spouse_working}
                            onChange={(e) => setFormData({ ...formData, spouse_working: e.target.checked })}
                            className="h-4 w-4 rounded border-slate-300"
                          />
                          <Label htmlFor="spouse_working" className="mb-0">Spouse Working</Label>
                        </div>
                      </>
                    )}
                    <div>
                      <Label>Number of Children</Label>
                      <Input
                        type="number"
                        min="0"
                        data-testid="add-employee-children-count"
                        value={formData.number_of_children}
                        onChange={(e) => setFormData({ ...formData, number_of_children: parseInt(e.target.value) || 0 })}
                      />
                    </div>
                    <div>
                      <Label>Disabled Children</Label>
                      <Input
                        type="number"
                        min="0"
                        value={formData.disabled_children}
                        onChange={(e) => setFormData({ ...formData, disabled_children: parseInt(e.target.value) || 0 })}
                      />
                    </div>
                    <div className="flex items-center space-x-2 pt-6">
                      <input
                        type="checkbox"
                        id="disabled_self"
                        checked={formData.disabled_self}
                        onChange={(e) => setFormData({ ...formData, disabled_self: e.target.checked })}
                        className="h-4 w-4 rounded border-slate-300"
                      />
                      <Label htmlFor="disabled_self" className="mb-0">Employee is Disabled</Label>
                    </div>
                    {formData.marital_status === 'married' && (
                      <div className="flex items-center space-x-2 pt-6">
                        <input
                          type="checkbox"
                          id="disabled_spouse"
                          checked={formData.disabled_spouse}
                          onChange={(e) => setFormData({ ...formData, disabled_spouse: e.target.checked })}
                          className="h-4 w-4 rounded border-slate-300"
                        />
                        <Label htmlFor="disabled_spouse" className="mb-0">Spouse is Disabled</Label>
                      </div>
                    )}
                  </div>
                  <div className="mt-4">
                    <h5 className="text-sm font-semibold text-slate-800 dark:text-slate-200 mb-2">LHDN TP1 Relief Declarations</h5>
                    <TaxReliefForm value={formData.tax_reliefs} onChange={(tr) => setFormData((f) => ({ ...f, tax_reliefs: tr }))} onErrorsChange={setReliefErrors} maritalStatus={formData.marital_status} childrenCount={Number(formData.number_of_children || 0) + Number(formData.disabled_children || 0)} />
                  </div>
                </div>

                {/* Statutory Information */}
                <div className="border-t border-slate-200 pt-4 mt-4">
                  <h4 className="font-semibold text-slate-900 mb-3">Statutory Contributions</h4>
                  <StatutoryContributionsForm
                    value={formData.statutory_settings}
                    onChange={(ss) => setFormData((f) => ({ ...f, statutory_settings: ss }))}
                    nationality={formData.nationality}
                    icNumber={formData.ic_number}
                    wages={Number(formData.salary_basic || 0) + Number(formData.transport_allowance || 0) + Number(formData.meal_allowance || 0) + Number(formData.housing_allowance || 0) + Number(formData.other_allowance || 0)}
                  />
                  <h4 className="font-semibold text-slate-900 mb-3 mt-4">Statutory Numbers</h4>
                  <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>EPF Number</Label>
                    <Input
                      value={formData.epf_number}
                      onChange={(e) => setFormData({ ...formData, epf_number: e.target.value })}
                      placeholder="EPF123456"
                    />
                  </div>
                  <div>
                    <Label>SOCSO Number</Label>
                    <Input
                      value={formData.socso_number}
                      onChange={(e) => setFormData({ ...formData, socso_number: e.target.value })}
                      placeholder="SOCSO123456"
                    />
                  </div>
                  <div>
                    <Label>Bank Name</Label>
                    <Input
                      value={formData.bank_name}
                      onChange={(e) => setFormData({ ...formData, bank_name: e.target.value })}
                      placeholder="Maybank"
                    />
                  </div>
                  <div>
                    <Label>Bank Account</Label>
                    <Input
                      value={formData.bank_account}
                      onChange={(e) => setFormData({ ...formData, bank_account: e.target.value })}
                      placeholder="123456789012"
                    />
                  </div>
                </div>
                </div>

                {/* Send Invitation Option */}
                <div className="border-t border-slate-200 dark:border-slate-700 pt-4 mt-4">
                  <div className="bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-700/50 rounded-xl p-4">
                    <div className="flex items-start space-x-3">
                      <input
                        type="checkbox"
                        id="sendInvitation"
                        checked={sendInvitation}
                        onChange={(e) => setSendInvitation(e.target.checked)}
                        data-testid="send-invitation-checkbox"
                        className="h-5 w-5 rounded border-slate-300 dark:border-slate-600 text-emerald-600 mt-0.5 bg-white dark:bg-slate-800"
                      />
                      <div className="flex-1">
                        <Label htmlFor="sendInvitation" className="text-slate-900 dark:text-slate-100 font-semibold cursor-pointer">
                          Send Invitation Email to Employee
                        </Label>
                        <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
                          Employee will receive an email with an activation link to set their password and access the system. 
                          The invitation link will be valid for 7 days.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                </div>
                <div className="flex justify-end space-x-3 px-6 py-4 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 shrink-0">
                  <Button type="button" variant="outline" onClick={() => {
                    setDialogOpen(false);
                    resetForm();
                  }}>
                    Cancel
                  </Button>
                  <Button type="submit" data-testid="add-employee-submit-btn" className="bg-slate-900 hover:bg-slate-800 dark:bg-emerald-600 dark:hover:bg-emerald-700">
                    Add Employee
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
          )}
        </div>
      </div>

      {/* Active Employees View */}
      {activeTab === 'active' && (
        <>
      {/* Search and Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
          <Input
            data-testid="search-employees-input"
            placeholder="Search by name, email, IC number, department..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 h-11"
          />
        </div>
        
        {/* Sort Dropdown */}
        <select
          value={sortOrder}
          onChange={(e) => setSortOrder(e.target.value)}
          className="h-11 px-4 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100"
        >
          <option value="name_asc">Name (A-Z)</option>
          <option value="name_desc">Name (Z-A)</option>
          <option value="department_asc">Department (A-Z)</option>
          <option value="department_desc">Department (Z-A)</option>
          <option value="join_date_newest">Newest First</option>
          <option value="join_date_oldest">Oldest First</option>
        </select>

        {/* Status Filter Dropdown */}
        <div className="relative">
          <Button 
            variant="outline" 
            className="h-11"
            data-testid="status-filter-btn"
            onClick={() => setShowFilterDropdown(!showFilterDropdown)}
          >
            <Filter className="h-4 w-4 mr-2" />
            {filterStatus === 'all' ? 'All Status' :
              filterStatus === 'inactive' ? 'Pending Activation' :
              filterStatus === 'on_notice_period' ? 'On Notice Period' :
              filterStatus === 'terminated' ? 'Terminated' :
              filterStatus === 'pending_joining' ? 'Pending Joining' :
              filterStatus.charAt(0).toUpperCase() + filterStatus.slice(1)}
          </Button>
          
          {showFilterDropdown && (
            <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl shadow-lg z-50" data-testid="status-filter-dropdown">
              <button
                data-testid="status-filter-all"
                onClick={() => { setFilterStatus('all'); setShowFilterDropdown(false); }}
                className={`w-full text-left px-4 py-2 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors ${filterStatus === 'all' ? 'bg-slate-100 dark:bg-slate-700' : ''}`}
              >
                All Status
              </button>
              <button
                data-testid="status-filter-active"
                onClick={() => { setFilterStatus('active'); setShowFilterDropdown(false); }}
                className={`w-full text-left px-4 py-2 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors ${filterStatus === 'active' ? 'bg-slate-100 dark:bg-slate-700' : ''}`}
              >
                Active
              </button>
              <button
                data-testid="status-filter-pending-joining"
                onClick={() => { setFilterStatus('pending_joining'); setShowFilterDropdown(false); }}
                className={`w-full text-left px-4 py-2 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors ${filterStatus === 'pending_joining' ? 'bg-slate-100 dark:bg-slate-700' : ''}`}
              >
                Pending Joining
              </button>
              {/* Iter133 — Added so HR can quickly surface employees in
                  their notice window. Requested by Shikha 19-Jun-2026. */}
              <button
                data-testid="status-filter-on-notice"
                onClick={() => { setFilterStatus('on_notice_period'); setShowFilterDropdown(false); }}
                className={`w-full text-left px-4 py-2 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors ${filterStatus === 'on_notice_period' ? 'bg-slate-100 dark:bg-slate-700' : ''}`}
              >
                On Notice Period
              </button>
              {/* Iter133 — Renamed "Inactive" → "Pending Activation" to
                  match the badge label (employees with status='inactive'
                  are those with un-accepted invitations, NOT a status HR
                  sets manually). Reported by Shikha 19-Jun-2026:
                  "filter shows Inactive but no UI to change employees
                  to Inactive." */}
              <button
                data-testid="status-filter-inactive"
                onClick={() => { setFilterStatus('inactive'); setShowFilterDropdown(false); }}
                className={`w-full text-left px-4 py-2 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors ${filterStatus === 'inactive' ? 'bg-slate-100 dark:bg-slate-700' : ''}`}
                title="Employees who have not yet accepted their login invitation"
              >
                Pending Activation
              </button>
              {/* Iter133 — Clicking "Terminated" in the filter dropdown
                  switches to the dedicated Terminated tab (which renders
                  the red-themed table over `terminatedEmployees`) instead
                  of filtering the active list — which was returning 0
                  results because `employees` deliberately excludes
                  terminated. Reported by Shikha 19-Jun-2026: clicking
                  "Terminated (3)" tab or filter still showed "No employees
                  found". */}
              <button
                data-testid="status-filter-terminated"
                onClick={() => { setActiveTab('terminated'); setFilterStatus('all'); setShowFilterDropdown(false); }}
                className={`w-full text-left px-4 py-2 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors ${filterStatus === 'terminated' ? 'bg-slate-100 dark:bg-slate-700' : ''}`}
              >
                Terminated
              </button>
            </div>
          )}
        </div>

        {/* View Toggle */}
        <div className="flex border border-slate-300 dark:border-slate-600 rounded-md overflow-hidden">
          <button
            onClick={() => setViewMode('grid')}
            className={`p-2.5 transition-colors ${
              viewMode === 'grid'
                ? 'bg-slate-900 dark:bg-slate-700 text-white'
                : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700'
            }`}
            title="Grid View"
          >
            <Grid3x3 className="h-5 w-5" />
          </button>
          <button
            onClick={() => setViewMode('list')}
            className={`p-2.5 transition-colors ${
              viewMode === 'list'
                ? 'bg-slate-900 dark:bg-slate-700 text-white'
                : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700'
            }`}
            title="List View"
          >
            <List className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* TC-EMP-011, TC-EMP-014, TC-EMP-016, TC-EMP-042: Additional Filters Row */}
      <div className="flex items-center space-x-4 flex-wrap gap-2">
        {/* TC-EMP-011: Department Filter */}
        <select
          data-testid="department-filter"
          value={filterDepartment}
          onChange={(e) => setFilterDepartment(e.target.value)}
          className="h-10 px-3 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm text-slate-900 dark:text-slate-100"
        >
          <option value="all">All Departments</option>
          {departments.map(dept => (
            <option key={dept} value={dept}>{dept}</option>
          ))}
        </select>

        {/* TC-EMP-014: Position Filter */}
        <select
          data-testid="position-filter"
          value={filterPosition}
          onChange={(e) => setFilterPosition(e.target.value)}
          className="h-10 px-3 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm text-slate-900 dark:text-slate-100"
        >
          <option value="all">All Positions</option>
          {positions.map(pos => (
            <option key={pos} value={pos}>{pos}</option>
          ))}
        </select>

        {/* TC-EMP-016: Clear Filters Button */}
        {hasActiveFilters && (
          <Button
            data-testid="clear-filters-btn"
            variant="ghost"
            size="sm"
            onClick={clearFilters}
            className="text-slate-600 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-100"
          >
            <X className="h-4 w-4 mr-1" />
            Clear Filters
          </Button>
        )}

        <div className="flex-1" />

        {/* TC-EMP-042: Export Button */}
        <Button
          data-testid="export-employees-btn"
          variant="outline"
          size="sm"
          onClick={exportAllEmployees}
          className="text-slate-600 hover:text-slate-900"
        >
          <Download className="h-4 w-4 mr-2" />
          Export CSV
        </Button>

        {/* Results count */}
        <span className="text-sm text-slate-500 dark:text-slate-400">
          {filteredEmployees.length} employee{filteredEmployees.length !== 1 ? 's' : ''} found
        </span>
      </div>

      {/* Employees Grid View */}
      {viewMode === 'grid' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {paginatedEmployees.map((employee) => (
            <div
              key={employee.employee_id}
              data-testid={`employee-card-${employee.employee_id}`}
              onClick={() => navigate(`/dashboard/employees/${employee.employee_id}`)}
              className="bg-white dark:bg-slate-800 rounded-xl border border-slate-100 dark:border-slate-700 p-6 shadow-sm card-hover cursor-pointer transition-all hover:shadow-md hover:border-slate-300 dark:hover:border-slate-500"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className="h-12 w-12 rounded-full bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center">
                    <span className="text-white font-semibold text-lg">
                      {employee.full_name.charAt(0)}
                    </span>
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900 dark:text-slate-100">{employee.full_name}</h3>
                    <p className="text-xs text-slate-500 dark:text-slate-400">{employee.employee_id}</p>
                  </div>
                </div>
                <span className={`status-badge ${
                  employee.status === 'active' ? 'status-active'
                    : employee.status === 'pending_joining' ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300 px-2 py-0.5 rounded-full text-xs font-medium'
                    : employee.status === 'on_notice_period' ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300 px-2 py-0.5 rounded-full text-xs font-medium'
                    : 'status-inactive'
                }`}>
                  {employee.status === 'pending_joining' ? 'Pending Joining'
                    : employee.status === 'inactive' ? 'Pending Activation'
                    : employee.status === 'on_notice_period' ? 'On Notice Period'
                    : employee.status}
                </span>
              </div>

              {/* Iter67 — small badge for active employees who still don't have
                  a login account. Helps HR scan and follow up. Uses the
                  server-computed has_user_account flag so it accounts for
                  orphan users (account exists but employee.user_id is empty). */}
              {employee.status === 'active' && employee.has_user_account === false && (
                <div
                  className="mb-3 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-orange-50 text-orange-700 border border-orange-200 dark:bg-orange-900/20 dark:text-orange-300 dark:border-orange-800"
                  data-testid={`no-login-badge-${employee.employee_id}`}
                  title="This active employee has not set up their MyHR Orbit login yet"
                >
                  <Mail className="h-3 w-3" /> No login set up
                </div>
              )}

              <div className="space-y-2 mb-4">
                <div className="flex items-center space-x-2 text-sm text-slate-600 dark:text-slate-400">
                  <MapPin className="h-4 w-4" />
                  <span>{employee.department || 'N/A'}</span>
                </div>
                <div className="flex items-center space-x-2 text-sm text-slate-600 dark:text-slate-400">
                  <Mail className="h-4 w-4" />
                  <span className="truncate">{employee.email}</span>
                </div>
                {employee.phone && (
                  <div className="flex items-center space-x-2 text-sm text-slate-600 dark:text-slate-400">
                    <Phone className="h-4 w-4" />
                    <span>{employee.phone}</span>
                  </div>
                )}
              </div>

              <div className="border-t border-slate-100 dark:border-slate-700 pt-4 flex items-center justify-between">
                <div>
                  <p className="text-xs text-slate-500 dark:text-slate-400">Position</p>
                  <p className="text-sm font-medium text-slate-900 dark:text-slate-100">{employee.position || 'N/A'}</p>
                </div>
                <div className="flex items-center space-x-2">
                  {canManage && (employee.status === 'pending_joining' || employee.status === 'inactive') && (
                    <button
                      onClick={(e) => handleActivateEmployee(e, employee)}
                      disabled={activatingId === employee.employee_id}
                      className="flex items-center text-sm text-emerald-600 hover:text-emerald-700 disabled:opacity-50"
                      title="Activate Employee — gives full access to leave/attendance/claims/payslips"
                      data-testid={`activate-employee-btn-${employee.employee_id}`}
                    >
                      {activatingId === employee.employee_id ? (
                        <RefreshCw className="h-4 w-4 mr-1 animate-spin" />
                      ) : (
                        <UserCheck className="h-4 w-4 mr-1" />
                      )}
                      Activate
                    </button>
                  )}
                  {canManage && employee.status === 'pending_joining' && (
                    <button
                      onClick={(e) => openOfferLetterModal(e, employee)}
                      className="flex items-center text-sm text-violet-600 hover:text-violet-700"
                      title="Upload and send the offer letter for signature"
                      data-testid={`send-offer-btn-${employee.employee_id}`}
                    >
                      <FileSignature className="h-4 w-4 mr-1" />
                      Send Offer
                    </button>
                  )}
                  {canManage && employee.status === 'inactive' && (
                    <button
                      onClick={(e) => handleResendInvitation(e, employee.employee_id)}
                      disabled={resendingId === employee.employee_id}
                      className="flex items-center text-sm text-amber-600 hover:text-amber-700 disabled:opacity-50"
                      title="Resend activation email"
                    >
                      {resendingId === employee.employee_id ? (
                        <RefreshCw className="h-4 w-4 mr-1 animate-spin" />
                      ) : (
                        <Send className="h-4 w-4 mr-1" />
                      )}
                      Resend
                    </button>
                  )}
                  {/* Iter67 — Send Login Invitation for ACTIVE employees who
                      were activated without ever receiving credentials. Uses
                      server-computed has_user_account flag. */}
                  {canManage && employee.status === 'active' && employee.has_user_account === false && (
                    <button
                      onClick={(e) => handleResendInvitation(e, employee.employee_id)}
                      disabled={resendingId === employee.employee_id}
                      className="flex items-center text-sm text-orange-600 hover:text-orange-700 disabled:opacity-50"
                      title="Send the login invitation email so this employee can sign in"
                      data-testid={`send-login-invite-btn-${employee.employee_id}`}
                    >
                      {resendingId === employee.employee_id ? (
                        <RefreshCw className="h-4 w-4 mr-1 animate-spin" />
                      ) : (
                        <Mail className="h-4 w-4 mr-1" />
                      )}
                      Send Login
                    </button>
                  )}
                  {canManage && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/dashboard/employees/${employee.employee_id}/edit`);
                      }}
                      className="flex items-center text-sm text-blue-600 hover:text-blue-700"
                    >
                      <Edit className="h-4 w-4 mr-1" />
                      Edit
                    </button>
                  )}
                  <div className="flex items-center text-sm text-emerald-600">
                    <Eye className="h-4 w-4 mr-1" />
                    View Details
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Employees List View */}
      {viewMode === 'list' && (
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-100 dark:border-slate-700 shadow-sm overflow-hidden overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700">
              <tr>
                <th className="text-left px-6 py-4 text-sm font-semibold text-slate-900 dark:text-slate-100">Employee</th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-slate-900 dark:text-slate-100">Department</th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-slate-900 dark:text-slate-100">Position</th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-slate-900 dark:text-slate-100">Contact</th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-slate-900 dark:text-slate-100">Status</th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-slate-900 dark:text-slate-100">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
              {paginatedEmployees.map((employee) => (
                <tr 
                  key={employee.employee_id} 
                  className="hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors cursor-pointer"
                  onClick={() => navigate(`/dashboard/employees/${employee.employee_id}`)}
                >
                  <td className="px-6 py-4">
                    <div className="flex items-center space-x-3">
                      <div className="h-10 w-10 rounded-full bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center">
                        <span className="text-white font-semibold text-sm">
                          {employee.full_name.charAt(0)}
                        </span>
                      </div>
                      <div>
                        <p className="font-medium text-slate-900 dark:text-slate-100">{employee.full_name}</p>
                        <p className="text-xs text-slate-500 dark:text-slate-400">{employee.employee_id}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-sm text-slate-900 dark:text-slate-100">{employee.department || 'N/A'}</span>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-sm text-slate-900 dark:text-slate-100">{employee.position || 'N/A'}</span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="space-y-1">
                      <p className="text-sm text-slate-600 dark:text-slate-400">{employee.email}</p>
                      {employee.phone && (
                        <p className="text-xs text-slate-500 dark:text-slate-400">{employee.phone}</p>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`status-badge ${
                      employee.status === 'active' ? 'status-active'
                        : employee.status === 'pending_joining' ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300 px-2 py-0.5 rounded-full text-xs font-medium'
                        : employee.status === 'on_notice_period' ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300 px-2 py-0.5 rounded-full text-xs font-medium'
                        : 'status-inactive'
                    }`}>
                      {employee.status === 'pending_joining' ? 'Pending Joining'
                        : employee.status === 'inactive' ? 'Pending Activation'
                        : employee.status === 'on_notice_period' ? 'On Notice Period'
                        : employee.status}
                    </span>
                    {/* Iter67 — sub-badge for active employees who still
                        don't have a login account. Uses server-computed
                        has_user_account so orphan-user emails are excluded. */}
                    {employee.status === 'active' && employee.has_user_account === false && (
                      <div
                        className="mt-1 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-orange-50 text-orange-700 border border-orange-200 dark:bg-orange-900/20 dark:text-orange-300 dark:border-orange-800"
                        data-testid={`no-login-row-badge-${employee.employee_id}`}
                        title="This active employee has not set up their MyHR Orbit login yet"
                      >
                        <Mail className="h-3 w-3" /> No login
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center space-x-2">
                      {canManage && (employee.status === 'pending_joining' || employee.status === 'inactive') && (
                        <button
                          onClick={(e) => handleActivateEmployee(e, employee)}
                          disabled={activatingId === employee.employee_id}
                          className="flex items-center text-sm text-emerald-600 hover:text-emerald-700 disabled:opacity-50"
                          title="Activate Employee — gives full access to leave/attendance/claims/payslips"
                          data-testid={`activate-employee-row-btn-${employee.employee_id}`}
                        >
                          {activatingId === employee.employee_id ? (
                            <RefreshCw className="h-4 w-4 mr-1 animate-spin" />
                          ) : (
                            <UserCheck className="h-4 w-4 mr-1" />
                          )}
                          Activate
                        </button>
                      )}
                      {canManage && employee.status === 'pending_joining' && (
                        <button
                          onClick={(e) => openOfferLetterModal(e, employee)}
                          className="flex items-center text-sm text-violet-600 hover:text-violet-700"
                          title="Upload and send the offer letter for signature"
                          data-testid={`send-offer-row-btn-${employee.employee_id}`}
                        >
                          <FileSignature className="h-4 w-4 mr-1" />
                          Send Offer
                        </button>
                      )}
                      {canManage && employee.status === 'inactive' && (
                        <button 
                          onClick={(e) => handleResendInvitation(e, employee.employee_id)}
                          disabled={resendingId === employee.employee_id}
                          className="flex items-center text-sm text-amber-600 hover:text-amber-700 disabled:opacity-50"
                          title="Resend activation email"
                        >
                          {resendingId === employee.employee_id ? (
                            <RefreshCw className="h-4 w-4 mr-1 animate-spin" />
                          ) : (
                            <Send className="h-4 w-4 mr-1" />
                          )}
                          Resend
                        </button>
                      )}
                      {/* Iter67 — Send Login Invitation for ACTIVE employees
                          who don't yet have a login account. Uses
                          server-computed has_user_account flag. */}
                      {canManage && employee.status === 'active' && employee.has_user_account === false && (
                        <button
                          onClick={(e) => handleResendInvitation(e, employee.employee_id)}
                          disabled={resendingId === employee.employee_id}
                          className="flex items-center text-sm text-orange-600 hover:text-orange-700 disabled:opacity-50"
                          title="Send the login invitation email so this employee can sign in"
                          data-testid={`send-login-invite-row-btn-${employee.employee_id}`}
                        >
                          {resendingId === employee.employee_id ? (
                            <RefreshCw className="h-4 w-4 mr-1 animate-spin" />
                          ) : (
                            <Mail className="h-4 w-4 mr-1" />
                          )}
                          Send Login
                        </button>
                      )}
                      {canManage && (
                        <button 
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/dashboard/employees/${employee.employee_id}/edit`);
                          }}
                          className="flex items-center text-sm text-blue-600 hover:text-blue-700"
                        >
                          <Edit className="h-4 w-4 mr-1" />
                          Edit
                        </button>
                      )}
                      <button className="flex items-center text-sm text-emerald-600 hover:text-emerald-700">
                        <Eye className="h-4 w-4 mr-1" />
                        View
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* TC-EMP-002: Pagination Controls — always shown when there is any data
          so the user can see total count + change page size. Prev/Next buttons
          are disabled when there's only a single page. */}
      {filteredEmployees.length > 0 && (
        <div className="flex items-center justify-between gap-3 flex-wrap px-4 py-3 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl" data-testid="pagination-bar">
          <div className="flex items-center space-x-2">
            <span className="text-sm text-slate-600 dark:text-slate-400">
              Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, filteredEmployees.length)} of {filteredEmployees.length} employees
            </span>
            <select
              data-testid="items-per-page"
              value={itemsPerPage}
              onChange={(e) => {
                setItemsPerPage(Number(e.target.value));
                setCurrentPage(1);
              }}
              className="h-8 px-2 text-sm rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800"
            >
              <option value={10}>10 per page</option>
              <option value={25}>25 per page</option>
              <option value={50}>50 per page</option>
              <option value={100}>100 per page</option>
            </select>
          </div>
          <div className="flex items-center space-x-2">
            <Button
              data-testid="pagination-prev"
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1 || totalPages <= 1}
            >
              Previous
            </Button>
            <div className="flex items-center space-x-1">
              {Array.from({ length: Math.min(5, Math.max(totalPages, 1)) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }
                return (
                  <Button
                    key={pageNum}
                    data-testid={`pagination-page-${pageNum}`}
                    variant={currentPage === pageNum ? "default" : "outline"}
                    size="sm"
                    onClick={() => setCurrentPage(pageNum)}
                    className="w-8 h-8 p-0"
                  >
                    {pageNum}
                  </Button>
                );
              })}
            </div>
            <Button
              data-testid="pagination-next"
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage >= totalPages || totalPages <= 1}
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {filteredEmployees.length === 0 && !loading && (
        <div className="text-center py-12 bg-white dark:bg-slate-800 rounded-xl border border-slate-100 dark:border-slate-700">
          <p className="text-slate-500 dark:text-slate-400">No employees found</p>
        </div>
      )}
        </>
      )}

      {/* Terminated Employees View */}
      {activeTab === 'terminated' && (
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-red-200 dark:border-red-800/50 shadow-sm overflow-hidden overflow-x-auto">
          {terminatedEmployees.length === 0 ? (
            <div className="text-center py-12">
              <UserX className="h-12 w-12 text-slate-400 mx-auto mb-4" />
              <p className="text-slate-500 dark:text-slate-400">No terminated employees</p>
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-red-50 dark:bg-red-900/20 border-b border-red-200 dark:border-red-800/50">
                <tr>
                  <th className="text-left px-6 py-4 text-sm font-semibold text-red-900 dark:text-red-400">Employee</th>
                  <th className="text-left px-6 py-4 text-sm font-semibold text-red-900 dark:text-red-400">Department</th>
                  <th className="text-left px-6 py-4 text-sm font-semibold text-red-900 dark:text-red-400">Termination Date</th>
                  <th className="text-left px-6 py-4 text-sm font-semibold text-red-900 dark:text-red-400">Type</th>
                  <th className="text-left px-6 py-4 text-sm font-semibold text-red-900 dark:text-red-400">Reason</th>
                  <th className="text-left px-6 py-4 text-sm font-semibold text-red-900 dark:text-red-400">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-red-100 dark:divide-red-800/30">
                {terminatedEmployees.map((employee) => (
                  <tr 
                    key={employee.employee_id} 
                    className="hover:bg-red-50 dark:hover:bg-red-900/10 transition-colors cursor-pointer"
                    onClick={() => navigate(`/dashboard/employees/${employee.employee_id}`)}
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-3">
                        <div className="h-10 w-10 rounded-full bg-gradient-to-br from-red-400 to-red-500 flex items-center justify-center">
                          <span className="text-white font-semibold text-sm">
                            {employee.full_name.charAt(0)}
                          </span>
                        </div>
                        <div>
                          <p className="font-medium text-slate-900 dark:text-slate-100">{employee.full_name}</p>
                          <p className="text-sm text-slate-500 dark:text-slate-400">{employee.employee_id}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-700 dark:text-slate-300">
                      {employee.department || 'N/A'}
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-700 dark:text-slate-300">
                      {employee.termination_details?.termination_date 
                        ? new Date(employee.termination_details.termination_date).toLocaleDateString('en-MY', { day: '2-digit', month: 'short', year: 'numeric' })
                        : 'N/A'}
                    </td>
                    <td className="px-6 py-4">
                      <span className="px-2 py-1 text-xs font-medium rounded-full bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400">
                        {employee.termination_details?.termination_type?.replace('_', ' ').toUpperCase() || 'N/A'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-700 dark:text-slate-300 max-w-xs truncate">
                      {employee.termination_details?.reason_for_leaving || 'N/A'}
                    </td>
                    <td className="px-6 py-4">
                      <button className="flex items-center text-sm text-red-600 hover:text-red-700">
                        <Eye className="h-4 w-4 mr-1" />
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* ---- Iter66: Send Offer Letter inline modal ---- */}
      {offerLetterModal.open && offerLetterModal.employee && (
        <div
          className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
          onClick={closeOfferLetterModal}
          data-testid="send-offer-modal-backdrop"
        >
          <div
            className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl max-w-lg w-full p-6"
            onClick={(e) => e.stopPropagation()}
            data-testid="send-offer-modal"
          >
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Send Offer Letter</h3>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  To <strong>{offerLetterModal.employee.full_name}</strong> &lt;{offerLetterModal.employee.email}&gt;
                </p>
              </div>
              <button
                onClick={closeOfferLetterModal}
                className="text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"
                aria-label="Close"
                data-testid="send-offer-close-btn"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {!offerResult ? (
              <>
                <div className="space-y-4">
                  <div>
                    <Label className="text-sm">Offer letter (PDF) *</Label>
                    <Input
                      type="file"
                      accept="application/pdf,.pdf"
                      onChange={(e) => setOfferLetterFile(e.target.files?.[0] || null)}
                      data-testid="send-offer-file-input"
                      className="mt-1"
                    />
                    {offerLetterFile && (
                      <p className="text-xs text-slate-500 mt-1">
                        Picked: {offerLetterFile.name} ({Math.round(offerLetterFile.size / 1024)} KB)
                      </p>
                    )}
                  </div>
                  <div>
                    <Label className="text-sm">Message to candidate (optional)</Label>
                    <Input
                      value={offerLetterMessage}
                      onChange={(e) => setOfferLetterMessage(e.target.value)}
                      placeholder="Welcome to the team!"
                      data-testid="send-offer-message-input"
                      className="mt-1"
                    />
                  </div>
                  <p className="text-xs text-slate-500 bg-slate-50 dark:bg-slate-900 rounded p-3">
                    The candidate will receive an email with a secure signing link
                    (if email is configured) AND will see the request inside MyHR Orbit
                    when they log in. They will be able to <strong>Sign</strong> or
                    <strong> Decline</strong>.
                  </p>
                </div>
                <div className="flex justify-end gap-2 mt-6">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={closeOfferLetterModal}
                    disabled={offerSending}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    onClick={handleSendOfferLetter}
                    disabled={offerSending || !offerLetterFile}
                    className="bg-violet-600 hover:bg-violet-700 text-white disabled:opacity-50"
                    data-testid="send-offer-submit-btn"
                  >
                    {offerSending ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    ) : (
                      <Send className="h-4 w-4 mr-2" />
                    )}
                    {offerSending ? 'Sending…' : 'Send Offer Letter'}
                  </Button>
                </div>
              </>
            ) : (
              // Success state — show per-signer status + sign URL fallback
              <div className="space-y-3" data-testid="send-offer-result">
                <div className={`p-3 rounded-md text-sm ${
                  offerResult.summary_status === 'sent'
                    ? 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-800 dark:text-emerald-300'
                    : offerResult.summary_status === 'disabled'
                      ? 'bg-amber-50 dark:bg-amber-900/20 text-amber-800 dark:text-amber-300'
                      : 'bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-300'
                }`}>
                  {offerResult.summary_message || 'Offer letter sent.'}
                </div>
                {(offerResult.email_results || []).map((r, idx) => (
                  <div key={idx} className="border border-slate-200 dark:border-slate-700 rounded-md p-3 text-sm">
                    <div className="flex justify-between items-center">
                      <span className="font-medium">{r.name}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        r.status === 'sent'
                          ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300'
                          : r.status === 'email_not_configured'
                            ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'
                            : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'
                      }`}>
                        {r.status === 'sent' ? 'Email sent' : r.status === 'email_not_configured' ? 'Email not configured' : 'Email failed'}
                      </span>
                    </div>
                    <p className="text-xs text-slate-500 mt-0.5">{r.email}</p>
                    {r.sign_url && (
                      <div className="flex items-center gap-2 mt-2">
                        <Input value={`${window.location.origin}${r.sign_url}`} readOnly className="text-xs" />
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            navigator.clipboard.writeText(`${window.location.origin}${r.sign_url}`);
                            toast.success('Sign link copied');
                          }}
                          data-testid={`copy-sign-link-${idx}`}
                        >
                          Copy
                        </Button>
                      </div>
                    )}
                  </div>
                ))}
                <div className="flex justify-end mt-4">
                  <Button onClick={closeOfferLetterModal} data-testid="send-offer-done-btn">Done</Button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Iter88k — Resignation review confirmation dialog */}
      <Dialog
        open={!!resignReviewDialog}
        onOpenChange={(open) => {
          if (!open) {
            setResignReviewDialog(null);
            setResignReviewMode(null);
            setResignReviewNote('');
          }
        }}
      >
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className={resignReviewMode === 'approve' ? 'text-emerald-600' : 'text-red-600'}>
              {resignReviewMode === 'approve' ? 'Approve' : 'Reject'} Resignation
            </DialogTitle>
          </DialogHeader>
          {resignReviewDialog && (
            <div className="space-y-3">
              <div className="p-3 bg-slate-50 dark:bg-slate-900 rounded-lg text-sm">
                <p><strong>Employee:</strong> {resignReviewDialog.employee_name} ({resignReviewDialog.employee_id})</p>
                <p><strong>Last working day:</strong> {resignReviewDialog.last_working_day}</p>
                <p><strong>Notice (as submitted):</strong> {resignReviewDialog.notice_period_days || 0} day(s)</p>
                <p className="mt-2"><strong>Reason:</strong> {resignReviewDialog.reason}</p>
              </div>
              {resignReviewMode === 'approve' && (
                <div>
                  <Label>Notice Period (days) — HR may override</Label>
                  <Input
                    type="number"
                    min="0"
                    value={resignReviewNotice}
                    data-testid="resign-review-notice-input"
                    onChange={(e) => setResignReviewNotice(Number(e.target.value) || 0)}
                    className="mt-1"
                  />
                  {/* Iter88r — Live preview of the recalculated last
                      working day. Helps HR confirm the offboarding date
                      shifts as expected before they click Confirm. */}
                  {(() => {
                    const submittedAt = resignReviewDialog?.submitted_at;
                    if (!submittedAt) return null;
                    const submittedUtc = new Date(submittedAt);
                    if (Number.isNaN(submittedUtc.getTime())) return null;
                    // Convert UTC → MYT (UTC+8) → take date
                    const myt = new Date(submittedUtc.getTime() + 8 * 3600 * 1000);
                    myt.setUTCDate(myt.getUTCDate() + (Number(resignReviewNotice) || 0));
                    const computedLwd = myt.toISOString().slice(0, 10);
                    const originalLwd = resignReviewDialog.last_working_day;
                    const changed = computedLwd !== originalLwd;
                    return (
                      <p
                        className={`text-xs mt-2 ${changed ? 'text-amber-700 dark:text-amber-300' : 'text-slate-500'}`}
                        data-testid="resign-review-lwd-preview"
                      >
                        New last working day: <strong>{computedLwd}</strong>
                        {changed && (
                          <> (shifted from <span className="line-through">{originalLwd}</span>)</>
                        )}
                      </p>
                    );
                  })()}
                </div>
              )}
              {resignReviewMode === 'approve' ? (
                <div className="flex items-start gap-2 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700/40 rounded-lg text-sm text-amber-800 dark:text-amber-200">
                  <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                  <p>
                    Approving will <strong>mark this employee as terminated</strong> on their last working day and trigger
                    proration in payroll. This action cannot be undone from the UI.
                  </p>
                </div>
              ) : null}
              <div>
                <Label>{resignReviewMode === 'approve' ? 'HR Note (optional)' : 'Reason for Rejection *'}</Label>
                <textarea
                  data-testid="resign-review-note"
                  value={resignReviewNote}
                  onChange={(e) => setResignReviewNote(e.target.value)}
                  placeholder={
                    resignReviewMode === 'approve'
                      ? 'e.g., All assets returned, exit interview scheduled.'
                      : 'e.g., Please serve the full 60-day notice period.'
                  }
                  rows={3}
                  className="mt-1 w-full p-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-800"
                />
              </div>
            </div>
          )}
          <div className="flex justify-end gap-2 mt-4">
            <Button
              variant="outline"
              onClick={() => {
                setResignReviewDialog(null);
                setResignReviewMode(null);
                setResignReviewNote('');
              }}
              disabled={resignReviewSubmitting}
              data-testid="resign-review-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              onClick={submitResignReview}
              disabled={resignReviewSubmitting}
              data-testid="resign-review-confirm-btn"
              className={
                resignReviewMode === 'approve'
                  ? 'bg-emerald-600 hover:bg-emerald-700 text-white'
                  : 'bg-red-600 hover:bg-red-700 text-white'
              }
            >
              {resignReviewSubmitting ? 'Saving…' : `Confirm ${resignReviewMode === 'approve' ? 'Approval' : 'Rejection'}`}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// TC-EMP-048 / TC-EMP-049: Bulk Import (CSV) Button + Dialog
function BulkImportButton({ onImported }) {
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [dryRun, setDryRun] = useState(true);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const handleDownloadTemplate = async () => {
    try {
      const token =
        localStorage.getItem('hrms_token') ||
        sessionStorage.getItem('hrms_token') ||
        '';
      const resp = await fetch(`${API}/employees/bulk-import/template`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!resp.ok) throw new Error('Template request failed');
      const blob = await resp.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'employees_import_template.csv';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Failed to download CSV template');
    }
  };

  const handleImport = async () => {
    setError('');
    setResult(null);
    if (!file) {
      setError('Please choose a CSV file');
      return;
    }
    if (!file.name.toLowerCase().endsWith('.csv')) {
      setError('Only .csv files are accepted');
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('dry_run', dryRun ? 'true' : 'false');
      const resp = await axios.post(`${API}/employees/bulk-import`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setResult(resp.data);
      if (!dryRun && resp.data?.summary?.imported > 0) {
        toast.success(`Imported ${resp.data.summary.imported} employee(s)`);
        onImported?.();
      } else if (dryRun) {
        toast.info('Validation complete - review errors below before importing');
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Import failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Button
        variant="outline"
        className="gap-2"
        data-testid="bulk-import-btn"
        onClick={() => {
          setOpen(true);
          setResult(null);
          setError('');
          setFile(null);
        }}
      >
        <Plus className="h-4 w-4" />
        Bulk Import
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl" data-testid="bulk-import-dialog">
          <DialogHeader>
            <DialogTitle>Bulk Import Employees</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-slate-50 dark:bg-slate-900 p-4 rounded-md text-sm">
              <p className="font-medium mb-1">Required columns:</p>
              <p className="text-slate-600 dark:text-slate-400">
                full_name, email (plus optional: phone, ic_number, department,
                position, join_date, employment_type, salary_basic,
                salary_allowances, nationality, marital_status)
              </p>
              <Button
                size="sm"
                variant="link"
                onClick={handleDownloadTemplate}
                className="px-0"
                data-testid="bulk-import-download-template"
              >
                Download CSV template
              </Button>
            </div>

            <div>
              <Label htmlFor="bulk-csv-file">CSV file</Label>
              <Input
                id="bulk-csv-file"
                type="file"
                accept=".csv"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                data-testid="bulk-import-file"
              />
            </div>

            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={dryRun}
                onChange={(e) => setDryRun(e.target.checked)}
                data-testid="bulk-import-dry-run"
              />
              <span>Validate only (dry run) - don't create employees yet</span>
            </label>

            {error && (
              <p className="text-sm text-red-600" data-testid="bulk-import-error">
                {error}
              </p>
            )}

            {result && (
              <div className="border rounded-md p-3 text-sm space-y-2" data-testid="bulk-import-result">
                <div className="flex flex-wrap gap-3">
                  <span>Total rows: <b>{result.summary?.total_rows ?? 0}</b></span>
                  <span className="text-emerald-600">
                    {result.dry_run ? 'Would import' : 'Imported'}: <b>
                      {result.dry_run ? result.summary?.would_import : result.summary?.imported}
                    </b>
                  </span>
                  <span className="text-red-600">Errors: <b>{result.summary?.errors ?? 0}</b></span>
                  <span className="text-amber-600">Duplicates: <b>{result.summary?.skipped_duplicates ?? 0}</b></span>
                </div>
                {result.errors?.length > 0 && (
                  <div className="max-h-32 overflow-auto bg-red-50 dark:bg-red-900/20 p-2 rounded">
                    <p className="font-medium text-red-700 dark:text-red-300 mb-1">Row errors:</p>
                    <ul className="list-disc ml-5 space-y-0.5">
                      {result.errors.map((e, i) => (
                        <li key={i} className="text-red-700 dark:text-red-300">
                          Row {e.row}{e.email ? ` (${e.email})` : ''}: {e.errors.join('; ')}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {result.skipped?.length > 0 && (
                  <div className="max-h-32 overflow-auto bg-amber-50 dark:bg-amber-900/20 p-2 rounded">
                    <p className="font-medium text-amber-700 dark:text-amber-300 mb-1">Skipped duplicates:</p>
                    <ul className="list-disc ml-5 space-y-0.5">
                      {result.skipped.map((s, i) => (
                        <li key={i}>Row {s.row}{s.email ? ` (${s.email})` : ''}: {s.reason}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setOpen(false)} disabled={busy}>
                Close
              </Button>
              {/* TC-EMP-048: show both Validate (dry run) and Import buttons explicitly */}
              <Button
                variant="outline"
                onClick={() => { setDryRun(true); setTimeout(handleImport, 0); }}
                disabled={busy || !file}
                data-testid="bulk-import-validate-btn"
              >
                {busy && dryRun ? 'Validating...' : 'Validate'}
              </Button>
              <Button
                onClick={() => { setDryRun(false); setTimeout(handleImport, 0); }}
                disabled={busy || !file}
                data-testid="bulk-import-submit"
              >
                {busy && !dryRun ? 'Importing...' : 'Import'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}